/**
 * "Neon Circuit" — original composition for the level "First Steps".
 * 128 BPM, A minor, 32-beat (8-bar) loop: four-on-the-floor kick, offbeat
 * hats, Am–F–C–G bass, arpeggiated lead, sustained pad.
 */
import type { Track, NoteEvent } from '../../audio/track';

const A1 = 33,
  C2 = 36,
  F1 = 29,
  G1 = 31;
const A3 = 57,
  C4 = 60,
  E4 = 64,
  A4 = 69,
  F3 = 53,
  A3f = 57,
  C4f = 60,
  G3 = 55,
  B3 = 59,
  D4 = 62,
  E3 = 52,
  G4 = 67;

function fourOnFloor(beats: number): number[] {
  const out: number[] = [];
  for (let b = 0; b < beats; b++) out.push(b);
  return out;
}

function offbeatHats(beats: number): number[] {
  const out: number[] = [];
  for (let b = 0; b < beats; b++) out.push(b + 0.5);
  return out;
}

function snares(beats: number): number[] {
  const out: number[] = [];
  for (let b = 1; b < beats; b += 2) out.push(b);
  return out;
}

/** Eighth-note bass riff per 8-beat chord: root ×6 then fifth, octave. */
function bassBar(startBeat: number, root: number): NoteEvent[] {
  const out: NoteEvent[] = [];
  const pattern = [0, 0, 0, 0, 7, 0, 12, 7];
  for (let i = 0; i < 16; i++) {
    const semis = pattern[i % 8] ?? 0;
    out.push({
      beat: startBeat + i * 0.5,
      midi: root + semis,
      len: 0.45,
      vel: i % 4 === 0 ? 0.9 : 0.7,
    });
  }
  return out;
}

/** 16th-note arpeggio over a triad. */
function arp(startBeat: number, notes: number[], beats: number): NoteEvent[] {
  const out: NoteEvent[] = [];
  const steps = beats * 4;
  for (let i = 0; i < steps; i++) {
    const midi = notes[i % notes.length] ?? 69;
    out.push({ beat: startBeat + i * 0.25, midi, len: 0.22, vel: 0.5 + (i % 4 === 0 ? 0.2 : 0) });
  }
  return out;
}

const CHORDS: Array<{ root: number; triad: number[]; pad: number[] }> = [
  { root: A1, triad: [A3, C4, E4, A4], pad: [A3, C4, E4] }, // Am
  { root: F1, triad: [F3, A3f, C4f, F3 + 12], pad: [F3, A3f, C4f] }, // F
  { root: C2, triad: [G3, C4, E4, G4], pad: [E3, G3, C4] }, // C
  { root: G1, triad: [G3, B3, D4, G4], pad: [G3 - 12, B3, D4] }, // G
];

const LOOP_BEATS = 32;

const bass: NoteEvent[] = [];
const lead: NoteEvent[] = [];
const pad: NoteEvent[] = [];
CHORDS.forEach((ch, i) => {
  const start = i * 8;
  bass.push(...bassBar(start, ch.root));
  lead.push(...arp(start, ch.triad, 8));
  pad.push(...ch.pad.map((midi) => ({ beat: start, midi, len: 7.5, vel: 0.35 })));
});

export const NEON_CIRCUIT: Track = {
  id: 'neon-circuit',
  title: 'Neon Circuit',
  bpm: 128,
  loopBeats: LOOP_BEATS,
  kick: fourOnFloor(LOOP_BEATS),
  hat: offbeatHats(LOOP_BEATS),
  snare: snares(LOOP_BEATS),
  bass,
  lead,
  pad,
};
